import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const Header = styled.header`
  text-align: center;
  color: white;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 3rem;
  margin: 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  margin: 10px 0 0 0;
  opacity: 0.9;
`;

const BackButton = styled(Link)`
  position: absolute;
  top: 20px;
  left: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 10px 20px;
  text-decoration: none;
  color: white;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const MainContent = styled.main`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 30px;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const RightPanel = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 20px;
  height: fit-content;
`;

const ControlGroup = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  color: white;
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  margin-bottom: 10px;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 5px;
  background: ${props => {
    if (props.$variant === 'danger') return '#ff6b6b';
    if (props.$variant === 'warning') return '#ffd43b';
    if (props.$variant === 'success') return '#51cf66';
    return '#4ecdc4';
  }};
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 8px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const VisualizationContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 30px;
  min-height: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const HashTable = styled.div`
  display: grid;
  grid-template-columns: repeat(${props => props.$size}, 1fr);
  gap: 8px;
  max-width: 100%;
  overflow-x: auto;
  margin: 20px 0;
`;

const HashBucket = styled.div`
  background: ${props => {
    if (props.$isCurrent) return '#ffd43b';
    if (props.$isVisited) return '#4ecdc4';
    if (props.$isCollision) return '#ff6b6b';
    return 'rgba(255, 255, 255, 0.9)';
  }};
  color: #333;
  padding: 15px 10px;
  border-radius: 8px;
  text-align: center;
  min-width: 80px;
  min-height: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  border: ${props => props.$isCurrent ? '2px solid #ff6b6b' : 'none'};
  position: relative;
`;

const BucketIndex = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 5px;
  opacity: 0.7;
`;

const BucketContent = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
  word-break: break-word;
  max-width: 100%;
`;

const CollisionIndicator = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  background: #ff6b6b;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 600;
`;



const StepInfo = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-bottom: 20px;
`;

const StepTitle = styled.h4`
  color: white;
  margin: 0 0 10px 0;
  font-size: 1rem;
`;



const StatusBar = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-bottom: 20px;
`;

const StatusText = styled.p`
  color: white;
  margin: 5px 0;
  font-size: 0.9rem;
`;

const StatsPanel = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-bottom: 20px;
`;

const StatsTitle = styled.h4`
  color: white;
  margin: 0 0 10px 0;
  font-size: 1rem;
`;

const StatsText = styled.p`
  color: rgba(255, 255, 255, 0.9);
  margin: 5px 0;
  font-size: 0.9rem;
`;

const HashFunctionDisplay = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-bottom: 20px;
`;

const HashFunctionTitle = styled.h4`
  color: white;
  margin: 0 0 10px 0;
  font-size: 1rem;
`;

const HashFunctionText = styled.p`
  color: rgba(255, 255, 255, 0.9);
  margin: 5px 0;
  font-size: 0.9rem;
  font-family: 'Courier New', monospace;
  background: rgba(0, 0, 0, 0.2);
  padding: 8px;
  border-radius: 5px;
`;

function HashMapPage() {
  const [tableSize, setTableSize] = useState(10);
  const [hashMap, setHashMap] = useState({});
  const [keyInput, setKeyInput] = useState('');
  const [valueInput, setValueInput] = useState('');
  const [searchKey, setSearchKey] = useState('');
  const [deleteKey, setDeleteKey] = useState('');
  const [isVisualizing, setIsVisualizing] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [currentBucket, setCurrentBucket] = useState(null);
  const [operationHistory, setOperationHistory] = useState([]);
  const [hashFunction, setHashFunction] = useState('simple'); // simple, djb2, sdbm

  // Hash function implementations
  const hashFunctions = {
    simple: {
      name: 'Simple Hash',
      description: 'Basic modulo-based hash function',
      function: (key, size) => {
        if (typeof key === 'string') {
          let hash = 0;
          for (let i = 0; i < key.length; i++) {
            hash += key.charCodeAt(i);
          }
          return hash % size;
        }
        return key % size;
      }
    },
    djb2: {
      name: 'DJB2 Hash',
      description: 'Fast hash function with good distribution',
      function: (key, size) => {
        if (typeof key === 'string') {
          let hash = 5381;
          for (let i = 0; i < key.length; i++) {
            hash = ((hash << 5) + hash) + key.charCodeAt(i);
          }
          return Math.abs(hash) % size;
        }
        return key % size;
      }
    },
    sdbm: {
      name: 'SDBM Hash',
      description: 'Hash function with good avalanche properties',
      function: (key, size) => {
        if (typeof key === 'string') {
          let hash = 0;
          for (let i = 0; i < key.length; i++) {
            hash = key.charCodeAt(i) + (hash << 6) + (hash << 16) - hash;
          }
          return Math.abs(hash) % size;
        }
        return key % size;
      }
    }
  };

  const getHashValue = (key) => {
    return hashFunctions[hashFunction].function(key, tableSize);
  };

  const insertKeyValue = async (key, value) => {
    if (!key || !value) return;
    
    setIsVisualizing(true);
    setCurrentStep(`Calculating hash for key: "${key}"`);
    
    // Calculate hash
    const hash = getHashValue(key);
    setCurrentBucket(hash);
    
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setCurrentStep(`Inserting "${key}: ${value}" at bucket ${hash}`);
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Check for collision
    const hasCollision = hashMap[hash] && hashMap[hash].length > 0;
    
    const newHashMap = { ...hashMap };
    if (!newHashMap[hash]) {
      newHashMap[hash] = [];
    }
    
    // Check if key already exists
    const existingIndex = newHashMap[hash].findIndex(item => item.key === key);
    if (existingIndex !== -1) {
      newHashMap[hash][existingIndex].value = value;
      setCurrentStep(`Updated existing key "${key}" with new value "${value}"`);
    } else {
      newHashMap[hash].push({ key, value });
      setCurrentStep(`Inserted "${key}: ${value}" at bucket ${hash}${hasCollision ? ' (collision occurred)' : ''}`);
    }
    
    setHashMap(newHashMap);
    
    // Add to operation history
    setOperationHistory(prev => [...prev, {
      type: 'insert',
      key,
      value,
      hash,
      collision: hasCollision,
      timestamp: new Date().toLocaleTimeString()
    }]);
    
    await new Promise(resolve => setTimeout(resolve, 1000));
    setCurrentBucket(null);
    setCurrentStep('');
    setIsVisualizing(false);
  };

  const performSearch = async (key) => {
    if (!key) return;
    
    setIsVisualizing(true);
    setCurrentStep(`Searching for key: "${key}"`);
    
    // Calculate hash
    const hash = getHashValue(key);
    setCurrentBucket(hash);
    
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const bucket = hashMap[hash];
    if (!bucket || bucket.length === 0) {
      setCurrentStep(`Key "${key}" not found (bucket ${hash} is empty)`);
    } else {
      const item = bucket.find(item => item.key === key);
      if (item) {
        setCurrentStep(`Found "${key}: ${item.value}" at bucket ${hash}`);
      } else {
        setCurrentStep(`Key "${key}" not found in bucket ${hash}`);
      }
    }
    
    await new Promise(resolve => setTimeout(resolve, 2000));
    setCurrentBucket(null);
    setCurrentStep('');
    setIsVisualizing(false);
  };

  const performDelete = async (key) => {
    if (!key) return;
    
    setIsVisualizing(true);
    setCurrentStep(`Deleting key: "${key}"`);
    
    // Calculate hash
    const hash = getHashValue(key);
    setCurrentBucket(hash);
    
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const bucket = hashMap[hash];
    if (!bucket || bucket.length === 0) {
      setCurrentStep(`Key "${key}" not found (bucket ${hash} is empty)`);
    } else {
      const itemIndex = bucket.findIndex(item => item.key === key);
      if (itemIndex !== -1) {
        const newHashMap = { ...hashMap };
        newHashMap[hash].splice(itemIndex, 1);
        setHashMap(newHashMap);
        setCurrentStep(`Deleted "${key}" from bucket ${hash}`);
        
        // Add to operation history
        setOperationHistory(prev => [...prev, {
          type: 'delete',
          key,
          hash,
          timestamp: new Date().toLocaleTimeString()
        }]);
      } else {
        setCurrentStep(`Key "${key}" not found in bucket ${hash}`);
      }
    }
    
    await new Promise(resolve => setTimeout(resolve, 2000));
    setCurrentBucket(null);
    setCurrentStep('');
    setIsVisualizing(false);
  };

  const clearHashMap = () => {
    setHashMap({});
    setOperationHistory([]);
    setCurrentStep('');
    setCurrentBucket(null);
  };

  const generateRandomData = async () => {
    const randomData = [
      { key: 'apple', value: 'fruit' },
      { key: 'banana', value: 'fruit' },
      { key: 'carrot', value: 'vegetable' },
      { key: 'dog', value: 'animal' },
      { key: 'elephant', value: 'animal' },
      { key: 'fish', value: 'animal' },
      { key: 'grape', value: 'fruit' },
      { key: 'house', value: 'building' },
      { key: 'ice', value: 'water' },
      { key: 'jacket', value: 'clothing' }
    ];
    
    setIsVisualizing(true);
    
    // Clear existing data first
    setHashMap({});
    setOperationHistory([]);
    
    // Insert data sequentially with proper delays
    for (let i = 0; i < randomData.length; i++) {
      const item = randomData[i];
      
      setCurrentStep(`Generating data: ${item.key} -> ${item.value}`);
      
      // Calculate hash
      const hash = getHashValue(item.key);
      setCurrentBucket(hash);
      
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Update hash map directly without calling insertKeyValue
      setHashMap(prevHashMap => {
        const newHashMap = { ...prevHashMap };
        if (!newHashMap[hash]) {
          newHashMap[hash] = [];
        }
        newHashMap[hash].push({ key: item.key, value: item.value });
        return newHashMap;
      });
      
      // Add to operation history
      setOperationHistory(prev => [...prev, {
        type: 'insert',
        key: item.key,
        value: item.value,
        hash,
        collision: false, // No collision for new insertions
        timestamp: new Date().toLocaleTimeString()
      }]);
      
      await new Promise(resolve => setTimeout(resolve, 800));
    }
    
    setCurrentBucket(null);
    setCurrentStep('Random data generation complete!');
    setIsVisualizing(false);
  };

  const renderHashTable = () => {
    const buckets = [];
    
    for (let i = 0; i < tableSize; i++) {
      const bucket = hashMap[i] || [];
      const hasCollision = bucket.length > 1;
      const isCurrent = currentBucket === i;
      const isVisited = operationHistory.some(op => op.hash === i);
      
      buckets.push(
        <HashBucket
          key={i}
          $isCurrent={isCurrent}
          $isVisited={isVisited}
          $isCollision={hasCollision}
        >
          <BucketIndex>Bucket {i}</BucketIndex>
          <BucketContent>
            {bucket.length === 0 ? (
              <span style={{ opacity: 0.5 }}>Empty</span>
            ) : (
              bucket.map((item, index) => (
                <div key={index} style={{ marginBottom: '2px' }}>
                  <strong>{item.key}:</strong> {item.value}
                </div>
              ))
            )}
          </BucketContent>
          {hasCollision && <CollisionIndicator>{bucket.length}</CollisionIndicator>}
        </HashBucket>
      );
    }
    
    return (
      <div>
        <h3 style={{ color: 'white', textAlign: 'center', marginBottom: '20px' }}>
          Hash Table (Size: {tableSize})
        </h3>
        <HashTable $size={tableSize}>
          {buckets}
        </HashTable>
      </div>
    );
  };

  const getLoadFactor = () => {
    const totalItems = Object.values(hashMap).reduce((sum, bucket) => sum + bucket.length, 0);
    return (totalItems / tableSize).toFixed(2);
  };

  const getCollisionCount = () => {
    return Object.values(hashMap).filter(bucket => bucket.length > 1).length;
  };

  return (
    <PageContainer>
      <BackButton to="/">← Back to Home</BackButton>
      
      <Header>
        <Title>HashMap Visualizer</Title>
        <Subtitle>Visualize hash table operations with collision handling</Subtitle>
      </Header>
      
      <MainContent>
        <LeftPanel>
          <VisualizationContainer>
            {renderHashTable()}
          </VisualizationContainer>
        </LeftPanel>
        
        <RightPanel>
          <ControlGroup>
            <Label>Table Size</Label>
            <Input
              type="number"
              min="5"
              max="20"
              value={tableSize}
              onChange={(e) => setTableSize(parseInt(e.target.value) || 10)}
              disabled={isVisualizing}
            />
            
            <Label>Hash Function</Label>
            <select
              value={hashFunction}
              onChange={(e) => setHashFunction(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: 'none',
                borderRadius: '5px',
                background: 'rgba(255, 255, 255, 0.9)',
                fontSize: '1rem',
                marginBottom: '15px'
              }}
            >
              {Object.entries(hashFunctions).map(([key, func]) => (
                <option key={key} value={key}>{func.name}</option>
              ))}
            </select>
          </ControlGroup>
          
          <ControlGroup>
            <Label>Insert Key-Value</Label>
            <Input
              type="text"
              placeholder="Key"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              disabled={isVisualizing}
            />
            <Input
              type="text"
              placeholder="Value"
              value={valueInput}
              onChange={(e) => setValueInput(e.target.value)}
              disabled={isVisualizing}
            />
            <Button
              onClick={() => {
                insertKeyValue(keyInput, valueInput);
                setKeyInput('');
                setValueInput('');
              }}
              disabled={isVisualizing || !keyInput || !valueInput}
            >
              Insert
            </Button>
          </ControlGroup>
          
          <ControlGroup>
            <Label>Search Key</Label>
            <Input
              type="text"
              placeholder="Enter key to search"
              value={searchKey}
              onChange={(e) => setSearchKey(e.target.value)}
              disabled={isVisualizing}
            />
            <Button
              onClick={() => {
                performSearch(searchKey);
                setSearchKey('');
              }}
              disabled={isVisualizing || !searchKey}
              $variant="success"
            >
              Search
            </Button>
          </ControlGroup>
          
          <ControlGroup>
            <Label>Delete Key</Label>
            <Input
              type="text"
              placeholder="Enter key to delete"
              value={deleteKey}
              onChange={(e) => setDeleteKey(e.target.value)}
              disabled={isVisualizing}
            />
            <Button
              onClick={() => {
                performDelete(deleteKey);
                setDeleteKey('');
              }}
              disabled={isVisualizing || !deleteKey}
              $variant="danger"
            >
              Delete
            </Button>
          </ControlGroup>
          
          <ControlGroup>
            <Button
              onClick={generateRandomData}
              disabled={isVisualizing}
              $variant="warning"
            >
              Generate Random Data
            </Button>
            <Button
              onClick={clearHashMap}
              disabled={isVisualizing}
              $variant="danger"
            >
              Clear All
            </Button>
          </ControlGroup>
          
          <HashFunctionDisplay>
            <HashFunctionTitle>Current Hash Function</HashFunctionTitle>
            <HashFunctionText>
              {hashFunctions[hashFunction].description}
            </HashFunctionText>
          </HashFunctionDisplay>
          
          <StatsPanel>
            <StatsTitle>Statistics</StatsTitle>
            <StatsText><strong>Load Factor:</strong> {getLoadFactor()}</StatsText>
            <StatsText><strong>Collisions:</strong> {getCollisionCount()}</StatsText>
            <StatsText><strong>Total Items:</strong> {Object.values(hashMap).reduce((sum, bucket) => sum + bucket.length, 0)}</StatsText>
          </StatsPanel>
          
          <StatusBar>
            <StatusText><strong>Status:</strong> {isVisualizing ? 'Processing...' : 'Ready'}</StatusText>
            {currentStep && (
              <StatusText><strong>Current:</strong> {currentStep}</StatusText>
            )}
          </StatusBar>
          
          {operationHistory.length > 0 && (
            <StepInfo>
              <StepTitle>Operation History</StepTitle>
              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {operationHistory.slice(-5).reverse().map((op, index) => (
                  <div key={index} style={{ marginBottom: '5px', fontSize: '0.8rem' }}>
                    <span style={{ color: op.type === 'insert' ? '#51cf66' : op.type === 'delete' ? '#ff6b6b' : '#4ecdc4' }}>
                      {op.type.toUpperCase()}
                    </span>
                    : {op.key} {op.timestamp}
                  </div>
                ))}
              </div>
            </StepInfo>
          )}
        </RightPanel>
      </MainContent>
    </PageContainer>
  );
}

export default HashMapPage;
